import classNames from 'classnames'
import IconInfo from 'front-end/src/components/common/icon/IconInfo'
import {
  StatusArchivedGrey,
  StatusProgressCompleted,
  StatusProgressDue,
  StatusProgressFailed,
  StatusProgressInProgress,
  StatusProgressNotStarted
} from 'front-end/src/components/common/status/Status'
import {
  PathwayEnrolmentStatusEnum,
  PathwayProgressStatus,
  PathwaysOverviewOrderColumn,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import { formatDateShort } from 'front-end/src/utils/dateHelpers'
import { truncate } from 'lodash'
import React, { FC, ReactNode, useContext, useState } from 'react'
import { Column } from 'react-table'
import { linkBuilder } from 'shared/linkBuilder'

import { t, Trans } from '@lingui/macro'

import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import Tooltip from '../../../components/common/tooltip/Tooltip'
import UserAvatar from '../../../components/common/user/user-avatar/UserAvatar'
import { PathwayArchiveUserEnrolment } from '../../manage-pathways-page/components/PathwayArchiveUserEnrolment'
import { AnalyticsContext } from '../AnalyticsContext'
import { IBaseOverviewFilter } from '../interfaces'
import { getPagesCount } from '../utils'
import PathwaysFilters from './components/PathwaysFilters'
import { DownloadPathwaysCSV } from './DownloadPathwaysCSV'
import { usePathwaysEnrolmentOverviewQuery } from './PathwaysEnrolmentOverview.codegen'
import { IconArchive } from 'front-end/src/components/common/icon/IconArchive'
import IconCheck from 'front-end/src/components/common/icon/IconCheck'
import IconDotsVertical from 'front-end/src/components/common/icon/IconDotsVertical'
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Box
} from 'front-end/src/components'
import { PathwayMarkAsComplete } from '../../manage-pathways-page/components/PathwayMarkAsComplete'
import { useRecalculatePathwayProgressMutation } from './PathwaysOverviewTable.codegen'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import IconReload from 'front-end/src/components/common/icon/IconReload'
import { AnalyticsPathwayEnrolmentsFilter } from '../../../graphql/public/PublicSchemaTypes'

interface IProps {
  userId?: number
}

const initialStateForPopups = {
  show: false,
  pathwayEnrolmentId: null,
  pathwayId: null
}

interface IEnrolmentModalState {
  show: boolean
  pathwayEnrolmentId: number | null
  pathwayId: number | null
}

export interface IOverviewFilter extends IBaseOverviewFilter {
  orderColumn: PathwaysOverviewOrderColumn
  progress: PathwayProgressStatus
}

const PathwaysOverviewTable: FC<IProps> = ({ userId }: IProps) => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const { campaignIds } = filter
  const [overviewTableFilters, setOverviewTableFilters] =
    useState<AnalyticsPathwayEnrolmentsFilter>({
      limit: 20,
      orderColumn: PathwaysOverviewOrderColumn.displayName,
      orderDirection: SqlOrderDirectionEnum.ASC,
      progress: PathwayProgressStatus.ALL,
      userId,
      campaignIds
    })

  const overviewTableQuery = {
    ...overviewTableFilters,
    page: filter.page,
    search: filter.search
  }

  const [removeEnrolmentModalState, setRemoveEnrolmentModalState] =
    useState<IEnrolmentModalState>(initialStateForPopups)

  const [completeEnrolmentModalState, setCompleteEnrolmentModalState] =
    useState<IEnrolmentModalState>(initialStateForPopups)

  const [recalculateProgress] = useRecalculatePathwayProgressMutation()
  React.useEffect(() => {
    setOverviewTableFilters((filters) => ({ ...filters, campaignIds }))
  }, [campaignIds])
  const {
    data,
    loading,
    refetch: refetchTable
  } = usePathwaysEnrolmentOverviewQuery({
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      pathwayEnrolmentsOverviewFilter: overviewTableQuery
    }
  })

  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Pathway`,
      accessor: 'pathway',
      id: PathwaysOverviewOrderColumn.pathwayName,
      className: 'tooltip',
      Cell: (row) => {
        const tooltip = (row.original.shared_at || row.original.importedAt) && (
          <Tooltip
            text={
              <>
                {/* we don't display shared_at for imported records */}
                {row.original.shared_at && !row.original.importedAt && (
                  <>
                    <Trans>Shared on</Trans>:{' '}
                    {formatDateShort(row.original.shared_at)}
                    <br />
                  </>
                )}
                {row.original.target_date && (
                  <>
                    <Trans>Due date</Trans>:{' '}
                    {formatDateShort(row.original.target_date)}
                    <br />
                  </>
                )}
                {row.original.importedAt && (
                  <>
                    <Trans>Imported on</Trans>:{' '}
                    {formatDateShort(row.original.importedAt)}
                    <br />
                  </>
                )}
                {row.original.importRecord?.source_name && (
                  <>
                    <Trans>Source</Trans>:{' '}
                    {row.original.importRecord.source_name}
                  </>
                )}
              </>
            }
          >
            <IconInfo />
          </Tooltip>
        )

        return (
          <>
            <Box className="overflowEllipsis">
              <strong className="dark">
                {truncate(row.value.title, { length: 120 })}
              </strong>
              {row.original.pathway.deleted_at && <StatusArchivedGrey />}
            </Box>
            <Box className="icons">{tooltip}</Box>
          </>
        )
      }
    },
    {
      Header: t`Started`,
      maxWidth: 160,
      width: 120,
      accessor: 'started_at',
      id: PathwaysOverviewOrderColumn.startedAt,
      Cell: (row) => formatDateShort(row.value)
    },
    {
      Header: t`Completed`,
      maxWidth: 160,
      width: 110,
      accessor: 'completed_at',
      id: PathwaysOverviewOrderColumn.completedAt,
      Cell: (row) => <span className="green">{formatDateShort(row.value)}</span>
    },
    {
      Header: t`Progress`,
      width: 90,
      accessor: 'progress',
      className: 'center',
      headerClassName: 'center',
      Cell: (row) => `${Math.round(row.value)}%`
    },

    {
      Header: t`Status`,
      sortable: false,
      accessor: '',
      maxWidth: 160,
      width: 115,
      Cell: (row) => {
        return (
          <>
            <Box className="result">{getStatus(row.original.status)}</Box>
          </>
        )
      }
    },
    {
      Header: t`Version`,
      sortable: false,
      width: 55,
      Cell: (row) => row.original.pathwayVersion?.version ?? ''
    },
    {
      Header: '',
      sortable: false,
      accessor: '',
      width: 70,
      className: 'menu overflowVisible',
      Cell: (row) => {
        return (
          !row.original.deleted_at && (
            <Menu>
              <MenuButton
                circular
                boxSize="37px"
                bg={'transparent'}
                border={0}
                onClick={(e) => {
                  e.stopPropagation() // Stop propagating click on row
                }}
                dataTest="KebabMenu"
              >
                <IconDotsVertical boxSize="18px" />
              </MenuButton>
              <MenuList data-test="MenuList">
                <MenuItem
                  icon={<IconArchive />}
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation()
                    setRemoveEnrolmentModalState({
                      show: true,
                      pathwayEnrolmentId: row.original.id,
                      pathwayId: row.original.pathway.id
                    })
                  }}
                  data-test="ArchiveButton"
                >
                  {t`Archive enrolment`}
                </MenuItem>
                {row.original.status !==
                  PathwayEnrolmentStatusEnum.COMPLETED && (
                  <>
                    <MenuItem
                      icon={<IconCheck fontSize="18px" />}
                      onClick={(clickEvent) => {
                        clickEvent.stopPropagation()
                        setCompleteEnrolmentModalState({
                          show: true,
                          pathwayEnrolmentId: row.original.id,
                          pathwayId: row.original.pathway.id
                        })
                      }}
                      data-test="CompleteEnrolmentButton"
                    >
                      {t`Complete enrolment`}
                    </MenuItem>
                    <MenuItem
                      icon={<IconReload fontSize="18px" />}
                      onClick={async (clickEvent) => {
                        clickEvent.stopPropagation()
                        await recalculateProgress({
                          variables: {
                            pathwayEnrolmentId: row.original.id
                          }
                        })
                        notification.info(
                          t`Pathway enrolment progress has been recalculated`
                        )
                        refetchTable()
                      }}
                      data-test="RecalculateEnrolmentButton"
                    >
                      {t`Recalculate progress`}
                    </MenuItem>
                  </>
                )}
              </MenuList>
            </Menu>
          )
        )
      }
    }
  ]

  if (!userId) {
    tableColumns.unshift({
      maxWidth: 280,
      className: null as any as string,
      Header: t`Student`,
      accessor: 'user',
      id: PathwaysOverviewOrderColumn.displayName,
      Cell: (row) => (
        <>
          <UserAvatar user={row.value} />
          <Box className="overflowEllipsis">{row.value.display_name}</Box>
          {row.value.deleted_at && <StatusArchivedGrey />}
        </>
      )
    })
  }

  let pagesCount = 1
  if (data && data.analytics) {
    pagesCount = getPagesCount(
      data.analytics.pathways?.enrolmentsCount,
      overviewTableQuery.limit ?? 0
    )
  }

  return (
    <Box>
      <TableExtendedOld
        loading={loading}
        manual
        data={data?.analytics.pathways?.enrolmentsOverview}
        tableHeader={t`Pathway records`}
        columns={tableColumns}
        className="-highlight AnalyticsTable AnalyticsTable__pathways"
        defaultPageSize={overviewTableQuery.limit ?? 0}
        showPageSizeOptions={false}
        showSearchFilter
        extendFilter={
          <PathwaysFilters
            progress={
              overviewTableQuery.progress ?? PathwayProgressStatus.NOT_STARTED
            }
            onProgressChange={(progress: PathwayProgressStatus) => {
              setOverviewTableFilters((prevState) => {
                return {
                  ...prevState,
                  page: 1,
                  progress
                }
              })
            }}
          />
        }
        resizable={false}
        minRows={1}
        pages={pagesCount}
        filterFields={['displayName']}
        getTrProps={(_state, row) => ({
          onClick: () =>
            navigateToAndResetPageAndSearch(
              linkBuilder.analyticsPathwayDetail(row.original.id),
              changeFilter
            ),
          className: classNames({
            archived: row && row.original.deleted_at,
            clickable: true
          }),
          ...DEFAULT_TR_PROPS
        })}
        paginationContentAfter={
          data &&
          data.analytics?.pathways && (
            <DownloadPathwaysCSV
              pathwaysOverviewFilter={overviewTableQuery as IOverviewFilter}
              pathwaysCount={data.analytics.pathways.enrolmentsCount}
            />
          )
        }
        defaultSortDesc
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn || 'displayName',
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setOverviewTableFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as PathwaysOverviewOrderColumn)
                : PathwaysOverviewOrderColumn.displayName,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />

      {completeEnrolmentModalState.show &&
        completeEnrolmentModalState.pathwayEnrolmentId &&
        completeEnrolmentModalState.pathwayId && (
          <PathwayMarkAsComplete
            pathwayEnrolmentId={completeEnrolmentModalState.pathwayEnrolmentId}
            onComplete={() => {
              setCompleteEnrolmentModalState(initialStateForPopups)
              refetchTable()
            }}
            onCanceled={() => {
              setCompleteEnrolmentModalState(initialStateForPopups)
            }}
          />
        )}

      {removeEnrolmentModalState.show &&
        removeEnrolmentModalState.pathwayEnrolmentId &&
        removeEnrolmentModalState.pathwayId && (
          <PathwayArchiveUserEnrolment
            pathwayEnrolmentId={removeEnrolmentModalState.pathwayEnrolmentId}
            onDeleted={() => {
              setRemoveEnrolmentModalState(initialStateForPopups)
              refetchTable()
            }}
            onCanceled={() => {
              setRemoveEnrolmentModalState(initialStateForPopups)
            }}
          />
        )}
    </Box>
  )
}

const getStatus = (status: PathwayEnrolmentStatusEnum): ReactNode => {
  switch (status) {
    case PathwayEnrolmentStatusEnum.COMPLETED:
      return <StatusProgressCompleted />
    case PathwayEnrolmentStatusEnum.FAILED:
      return <StatusProgressFailed />
    case PathwayEnrolmentStatusEnum.IN_PROGRESS:
      return <StatusProgressInProgress />
    case PathwayEnrolmentStatusEnum.NOT_STARTED:
      return <StatusProgressNotStarted />
    case PathwayEnrolmentStatusEnum.OVERDUE:
      return <StatusProgressDue status={PathwayEnrolmentStatusEnum.OVERDUE} />
  }
}

export default PathwaysOverviewTable
